import { useEffect, useRef, useState } from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import Slider from "react-slick";
import { useNavigate, useParams } from "react-router-dom";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const SLIDE_WIDTH = 240;

const ImageCarousel = ({ slides = [], onSelect }) => {
  const sliderRef = useRef(null);
  const trackRef = useRef(null);

  const { subProductSlug } = useParams();
  const navigate = useNavigate();

  const [current, setCurrent] = useState(0);
  const [ready, setReady] = useState(false);

  // ✅ REAL arrow control
  const [canGoPrev, setCanGoPrev] = useState(false);
  const [canGoNext, setCanGoNext] = useState(true);

  // ✅ Set initial slide
  useEffect(() => {
    if (!slides.length) return;

    let index = 0;

    if (subProductSlug) {
      const foundIndex = slides.findIndex(
        (s) => s.slug.split("/").pop() === subProductSlug
      );
      index = foundIndex !== -1 ? foundIndex : 0;
    }

    setCurrent(index);
    setReady(true);
  }, [slides, subProductSlug]);

  // ✅ Move to initial slide
  useEffect(() => {
    if (ready && sliderRef.current) {
      sliderRef.current.slickGoTo(current, true);
    }
  }, [ready]);

  // ✅ Detect scroll position (KEY FIX)
  const updateArrowState = () => {
    if (!trackRef.current) return;

    const track = trackRef.current;
    const transform = track.style.transform;

    // extract translateX value
    const match = transform.match(/translate3d\((-?\d+\.?\d*)px/);
    const x = match ? Math.abs(parseFloat(match[1])) : 0;

    const maxScroll = track.scrollWidth - track.parentElement.offsetWidth;

    setCanGoPrev(x > 5); // small buffer
    setCanGoNext(x < maxScroll - 5);
  };

  // ✅ Hook into slider DOM
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const track = slider.innerSlider?.list?.querySelector(".slick-track");
    if (!track) return;

    trackRef.current = track;

    const interval = setInterval(updateArrowState, 100); // lightweight polling

    return () => clearInterval(interval);
  }, [ready]);

  // ✅ Slider settings
  const settings = {
    infinite: false,
    speed: 400,
    slidesToScroll: 1,
    variableWidth: true,
    arrows: false,
    afterChange: (index) => {
      setCurrent(index);
      onSelect?.(slides[index]);
    },
  };

  if (!ready) return null;

  return (
    <div className="relative w-full group imageCarousel">
      {/* LEFT */}
      <button
        onClick={() => sliderRef.current?.slickPrev()}
        disabled={!canGoPrev}
        className={`absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 ${
          !canGoPrev
            ? "opacity-40 cursor-not-allowed"
            : "hover:bg-primary hover:text-white"
        }`}
      >
        <LeftOutlined />
      </button>

      {/* SLIDER */}
      <Slider ref={sliderRef} {...settings}>
        {slides.map((item, idx) => (
          <div
            key={item.id ?? idx}
            style={{ width: SLIDE_WIDTH }}
            className="px-3 cursor-pointer"
            onClick={() => {
              sliderRef.current?.slickGoTo(idx);
              navigate(`/activity/${item.slug}`);
            }}
          >
            <img
              src={`${process.env.REACT_APP_API_URL}${item.img}`}
              alt={item.title}
              className="w-full h-40 object-cover rounded-xl"
            />

            <div
              className={`pt-3 text-center font-medium ${
                current === idx ? "text-primary" : "text-black"
              }`}
            >
              {item.title}
            </div>
          </div>
        ))}
      </Slider>

      {/* RIGHT */}
      <button
        onClick={() => sliderRef.current?.slickNext()}
        disabled={!canGoNext}
        className={`absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 ${
          !canGoNext
            ? "opacity-40 cursor-not-allowed"
            : "hover:bg-primary hover:text-white"
        }`}
      >
        <RightOutlined />
      </button>
    </div>
  );
};

export default ImageCarousel;